import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import SafeIcon from '../../components/common/SafeIcon';
import VideoCard from '../../components/training/VideoCard';
import { TRAINING_ROW_SLUGS } from '../../constants/training';
import { useAuth } from '../../context/AuthContext';
import { fetchTrainingRowList } from '../../services/training';

export default function TrainingRowListPage() {
  const { rowId } = useParams();
  const { user } = useAuth();
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setNotFound(false);
      try {
        const result = await fetchTrainingRowList(rowId, user?.id);
        if (!active) return;
        if (result === null) {
          setNotFound(true);
          setRow(null);
        } else {
          setRow(result);
        }
      } catch (err) {
        console.error(err);
        if (active) {
          setNotFound(true);
          setRow(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => { active = false; };
  }, [rowId, user?.id]);

  if (
    !loading
    && rowId === TRAINING_ROW_SLUGS.CONTINUE
    && !user?.id
  ) {
    return <Navigate to="/app/entrenamientos" replace />;
  }

  if (!loading && notFound) {
    return <Navigate to="/app/entrenamientos" replace />;
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)] pb-28 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/app/entrenamientos"
            className="w-10 h-10 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-center text-[var(--text-main)] hover:scale-105 transition-transform"
          >
            <SafeIcon name="ArrowLeft" className="w-5 h-5" />
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl md:text-4xl font-black text-[var(--text-main)] uppercase tracking-tight italic truncate">
              {loading ? 'Cargando…' : row?.title}
            </h1>
            {!loading && row?.description && (
              <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-2xl">
                {row.description}
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-[var(--text-secondary)] font-bold uppercase tracking-widest">
            Cargando sesiones…
          </p>
        ) : row?.isComingSoon && row.sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
            <SafeIcon name="Clock" className="w-12 h-12 text-[var(--text-secondary)]/50" />
            <p className="text-[var(--text-secondary)] max-w-md">
              Este Camino estará disponible pronto. Vuelve más tarde para ver las sesiones.
            </p>
          </div>
        ) : row?.sessions.length === 0 ? (
          <p className="text-[var(--text-secondary)] py-12">
            No hay sesiones en esta lista por ahora.
          </p>
        ) : (
          <>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-6">
              {row.sessions.length} sesión{row.sessions.length === 1 ? '' : 'es'}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10">
              {row.sessions.map((session) => (
                <VideoCard key={session.id} session={session} fullWidth />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
